import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import styled from 'styled-components';

import {
  FlagIcon,
  BoltIcon,
  StopwatchIcon,
} from '../../../assets/svg/SVGComponents';
import frlMascot from '../../../assets/images/frl/frl-mascot.svg';
import { IBoardRound } from '../../../dtos/IBoardSnapshot';
import { formatSeconds, modalityLabel } from '../logic/labels';
import { colors, IconText } from '../styles';

export const BANNER_MS = 5000;
const SCORE_COUNT_MS = 1100;

const Stage = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: grid;
  place-items: center;
  padding: 1rem;
  overflow: hidden;
  pointer-events: none;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 30, 60, 0.55),
    rgba(0, 12, 28, 0.88)
  );

  @media (max-width: 768px) {
    position: fixed;
  }
`;

/** Feixes de luz girando atrás do cartão. */
const Rays = styled(motion.div)`
  position: absolute;
  width: 160vmax;
  height: 160vmax;
  left: 50%;
  top: 50%;
  margin: -80vmax 0 0 -80vmax;
  background: repeating-conic-gradient(
    from 0deg,
    rgba(255, 152, 0, 0.16) 0deg 8deg,
    transparent 8deg 22deg
  );
  mask-image: radial-gradient(circle, #000 0%, transparent 55%);
`;

const Card = styled(motion.div)`
  position: relative;
  width: min(92%, 80rem);
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 3vw;
  padding: 4vh 4vw;
  border-radius: 2rem;
  overflow: hidden;
  background: linear-gradient(
    125deg,
    ${colors.accent},
    ${colors.orange} 60%,
    #c85a0a
  );
  color: #1a0f00;
  box-shadow:
    0 3rem 8rem rgba(0, 0, 0, 0.55),
    inset 0 2px 0 rgba(255, 255, 255, 0.45);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    gap: 0.6rem;
    padding: 1.4rem 1.2rem;
    border-radius: 1.4rem;
  }
`;

const Shine = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 35%,
    rgba(255, 255, 255, 0.55) 50%,
    transparent 65%
  );
`;

const Mascot = styled(motion.img)`
  height: clamp(7rem, 30vh, 22rem);
  width: auto;
  filter: drop-shadow(0 1rem 1.5rem rgba(0, 0, 0, 0.35));

  @media (max-width: 768px) {
    height: 7rem;
  }
`;

const Title = styled(motion.div)`
  font-family: 'RedHatDisplay-Bold';
  font-size: clamp(1rem, 3vh, 2.2rem);
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Team = styled(motion.div)`
  font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
  font-size: clamp(2rem, 9vh, 7rem);
  line-height: 1;
  letter-spacing: -0.03em;
  margin: 1vh 0 0.6vh;
  overflow-wrap: anywhere;
`;

const School = styled(motion.div)`
  font-family: 'RedHatDisplay-Medium', 'RedHatDisplay-Regular';
  font-size: clamp(1rem, 3.2vh, 2.4rem);
  opacity: 0.85;
`;

const Score = styled(motion.div)`
  display: flex;
  align-items: baseline;
  gap: 0.25em;
  margin-top: 2vh;
  font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
  font-variant-numeric: tabular-nums;
  color: #fff;
  text-shadow: 0 0.06em 0 rgba(0, 0, 0, 0.18);

  b {
    font-size: clamp(3rem, 16vh, 12rem);
    line-height: 0.9;
    letter-spacing: -0.04em;
  }

  span {
    font-size: clamp(1.2rem, 4vh, 3rem);
    color: #1a0f00;
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Chips = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 2vh;

  > span {
    padding: 0.35em 0.9em;
    border-radius: 999px;
    background: rgba(0, 18, 42, 0.82);
    color: #fff;
    font-family: 'RedHatDisplay-Bold';
    font-size: clamp(0.9rem, 2.4vh, 1.8rem);
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

/** Conta de 0 até a pontuação da rodada. */
const useCountUp = (target: number, delayMs: number, enabled: boolean) => {
  const [value, setValue] = useState(enabled ? 0 : target);

  useEffect(() => {
    if (!enabled) {
      setValue(target);
      return undefined;
    }
    setValue(0);
    let frame = 0;
    const start = performance.now() + delayMs;
    const tick = (now: number) => {
      const t = Math.max(0, Math.min(1, (now - start) / SCORE_COUNT_MS));
      setValue(Math.round(target * (1 - (1 - t) ** 4)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, delayMs, enabled]);

  return value;
};

const rise = (delay: number, reduce: boolean | null) => ({
  initial: reduce ? { opacity: 0 } : { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: 'spring', bounce: 0, duration: 0.5, delay },
});

const BannerContent: React.FC<{ round: IBoardRound }> = ({ round }) => {
  const reduce = useReducedMotion();
  const score = useCountUp(round.total_score, 550, !reduce);

  return (
    <>
      {!reduce && (
        <Rays
          initial={{ opacity: 0, rotate: 0 }}
          animate={{ opacity: 1, rotate: 40 }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: 0.6 },
            rotate: { duration: BANNER_MS / 1000, ease: 'linear' },
          }}
        />
      )}
      <Card
        initial={
          reduce
            ? { opacity: 0 }
            : { opacity: 0, scale: 0.6, y: 60, filter: 'blur(16px)' }
        }
        animate={{ opacity: 1, scale: 1, y: 0, filter: 'blur(0px)' }}
        exit={
          reduce
            ? { opacity: 0 }
            : { opacity: 0, scale: 0.85, y: -40, filter: 'blur(12px)' }
        }
        transition={{ type: 'spring', bounce: 0.35, duration: 0.7 }}
      >
        {!reduce && (
          <Shine
            initial={{ x: '-110%' }}
            animate={{ x: '110%' }}
            transition={{ duration: 1.1, delay: 0.35, ease: 'easeInOut' }}
          />
        )}
        <Mascot
          src={frlMascot}
          alt=""
          initial={
            reduce ? { opacity: 0 } : { opacity: 0, scale: 0.4, rotate: -18 }
          }
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{
            type: 'spring',
            bounce: 0.55,
            duration: 0.8,
            delay: 0.15,
          }}
        />
        <div style={{ minWidth: 0, position: 'relative' }}>
          <Title {...rise(0.2, reduce)}>
            <IconText>
              <FlagIcon />
              Rodada encerrada · {modalityLabel(round.modality)}
            </IconText>
          </Title>
          <Team {...rise(0.3, reduce)}>{round.team_name}</Team>
          {round.school_name && (
            <School {...rise(0.38, reduce)}>{round.school_name}</School>
          )}
          <Score
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.7 }}
            animate={
              reduce
                ? { opacity: 1 }
                : { opacity: 1, scale: [0.7, 1, 1, 1.12, 1] }
            }
            transition={{
              duration: reduce ? 0.3 : 0.55 + SCORE_COUNT_MS / 1000 + 0.4,
              times: [0, 0.25, 0.75, 0.88, 1],
            }}
            style={{ transformOrigin: 'left center' }}
          >
            <b>{score}</b>
            <span>pts</span>
          </Score>
          <Chips {...rise(0.6, reduce)}>
            {round.arena_name && <span>{round.arena_name}</span>}
            {round.completion_time_seconds !== null && (
              <span>
                <IconText>
                  <StopwatchIcon />
                  {formatSeconds(round.completion_time_seconds)}
                </IconText>
              </span>
            )}
            {round.bonus_applied && (
              <span style={{ background: colors.gold, color: '#2a1a00' }}>
                <IconText>
                  <BoltIcon />
                  Bônus
                </IconText>
              </span>
            )}
          </Chips>
        </div>
      </Card>
    </>
  );
};

interface IRoundBannerProps {
  round: IBoardRound | null;
  onDone: () => void;
}

/**
 * Rodada encerrada (FE-09) em destaque de palco: a tela escurece, o cartão
 * da equipe chega com mola, a pontuação conta do zero e, depois de ~5s,
 * avisa a fila (FE-10) para seguir.
 */
const RoundBanner: React.FC<IRoundBannerProps> = ({ round, onDone }) => {
  useEffect(() => {
    if (!round) return undefined;
    const timer = setTimeout(onDone, BANNER_MS);
    return () => clearTimeout(timer);
  }, [round, onDone]);

  return (
    <AnimatePresence mode="wait">
      {round && (
        <Stage
          key={round.round_id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          role="status"
          aria-live="assertive"
        >
          <BannerContent round={round} />
        </Stage>
      )}
    </AnimatePresence>
  );
};

export default RoundBanner;
